import { Flex, Text, useTheme } from '@chakra-ui/react'
import React, { useState } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as ReLineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import iconFullscreen from '~/assets/svgs/icon-fullscreen.svg?react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import ChartWrapper from '~/pages/Dashboards/CallReporting/Shared/ChartWrapper'
import { textStyleKeys } from '~/themes/textStyles'

import { CallsReport } from '@mangovoice/types'

import { ReportingModal } from '../ReportingModal'

interface RechartsBarChartProps {
  chartData: CallsReport
  title?: string
  color?: string[]
  height?: number
  autoAdjustHeight?: boolean
  showAsPercent?: boolean
  hasModalButton?: boolean
}

const RechartsLineChart = ({
  chartData,
  title = 'Bar Chart',
  color = ['blue.500'],
  height = 300,
  autoAdjustHeight = true,
  showAsPercent = false,
  hasModalButton = false,
}: RechartsBarChartProps) => {
  const theme = useTheme()
  const defaultColor = theme.colors.blue[500]
  const barColors = color.map((color) => {
    const colorSegments = color.split('.')
    return colorSegments.reduce((acc, segment) => acc[segment], theme.colors)
  })
  const [isModalOpen, setIsModalOpen] = useState(false)

  const xAxisTextStyle = theme.textStyles[textStyleKeys.Body3]

  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return (
      <Flex
        direction="column"
        justify="center"
        align="center"
        bg="gray.100"
        borderRadius="md"
        height={`${height}px`}
      >
        <Text>No chart data available</Text>
      </Flex>
    )
  }

  const { data, meta } = chartData
  const seriesKeys = Object.keys(data[0]).filter((key) => key !== 'date')

  const getTickFormatter = () => {
    const { groupBy } = meta
    if (groupBy.includes('hour')) {
      return (tick: string) =>
        new Date(tick).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })
    } else {
      return (tick: string) => {
        const date = new Date(tick)
        if (isNaN(date.getTime())) return tick

        if (groupBy.includes('week')) {
          const day = date.getUTCDay()
          const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1)
          const weekStart = new Date(date)
          weekStart.setUTCDate(diff)
          return `Week of ${weekStart.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })}`
        }
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
      }
    }
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const originalDateStr = payload[0].payload.date
      const originalDate = new Date(originalDateStr)

      let header = ''

      if (meta.groupBy.includes('hour')) {
        header = originalDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })
      } else if (meta.groupBy.includes('week')) {
        const day = originalDate.getDay()
        const diff = originalDate.getDate() - day + (day === 0 ? -6 : 1)
        const weekStart = new Date(originalDate)
        weekStart.setDate(diff)
        header =
          'Week of ' +
          weekStart.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
      } else {
        header =
          originalDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
          }) +
          ' - ' +
          originalDate.toLocaleDateString('en-US', {
            weekday: 'long',
            timeZone: 'UTC',
          })
      }

      return (
        <Flex
          direction="column"
          bg="white"
          border="1px solid #ccc"
          borderRadius="4px"
          p="4px"
        >
          <Flex textStyle={textStyleKeys.Body1_Bold} color="gray.1300">
            {header}
          </Flex>
          {payload.map((item: any, index: number) => (
            <Flex
              key={index}
              color={item.color}
              textStyle={textStyleKeys.Body1}
            >
              {item.name}: {item.value}
            </Flex>
          ))}
        </Flex>
      )
    }
    return null
  }

  const formatter = getTickFormatter()

  const calculateLabelWidth = (labels: string[]) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return 0

    context.font = xAxisTextStyle
    const sampleLabel = formatter(labels[0])
    return context.measureText(sampleLabel).width
  }

  const xAxisLabelWidth = calculateLabelWidth(data.map((item) => item.date))

  const calculatedDy = xAxisLabelWidth / 2

  const chartMargin = {
    top: 10,
    right: 20,
    left: 0,
    bottom: seriesKeys.length > 1 ? 0 : xAxisLabelWidth,
  }
  const tickMargin = 10
  // TODO: Make this based on the screen size s/m/l/xl
  const recommendedInterval =
    data.length > 24 ? Math.ceil(data.length / 24) - 1 : 0

  // Adjust the height based on the label width so the chart doesn't shrink too much
  const adjustedHeight = autoAdjustHeight
    ? height + Math.max(xAxisLabelWidth - 40, 0)
    : height

  const renderLineChart = (isInModal = false) => {
    return (
      <ResponsiveContainer
        width="100%"
        height={isInModal ? '100%' : adjustedHeight - 80}
      >
        <ReLineChart data={data} margin={chartMargin}>
          <CartesianGrid stroke="#ddd" strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatter}
            tickMargin={tickMargin}
            interval={recommendedInterval}
            angle={-45}
            dx={-calculatedDy}
            dy={calculatedDy}
            tick={{
              fontSize: xAxisTextStyle.fontSize,
              fontFamily: xAxisTextStyle.fontFamily,
              fill: theme.colors.gray[700],
            }}
            stroke={theme.colors.gray[500]}
          />
          <YAxis
            domain={showAsPercent ? [0, 100] : [0, 'auto']}
            tickFormatter={
              showAsPercent ? (value: number) => `${value}%` : undefined
            }
            dx={-10}
            tick={{
              fontSize: xAxisTextStyle.fontSize,
              fontFamily: xAxisTextStyle.fontFamily,
              fill: theme.colors.gray[700],
            }}
            stroke={theme.colors.gray[500]}
          />
          <ReTooltip content={CustomTooltip} cursor={{ fill: '#aaaaaa33' }} />
          {seriesKeys.length > 1 && (
            <Legend
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ paddingTop: `${xAxisLabelWidth}px` }}
            />
          )}
          {seriesKeys.map((key, index) => (
            <Line
              key={key}
              dataKey={key}
              stroke={barColors[index] ?? barColors[0] ?? defaultColor}
              dot={true}
              strokeWidth={2}
            />
          ))}
        </ReLineChart>
      </ResponsiveContainer>
    )
  }

  return (
    <>
      <ChartWrapper height={`${adjustedHeight}px`}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mb="0.5rem"
          mt="-10px"
        >
          <Text textStyle={textStyleKeys.Body2_Bold}>{title}</Text>
          {hasModalButton && (
            <QuickActionButton
              actionIcon={iconFullscreen}
              tooltipText="Fullscreen"
              variant={QuickActionButtonVariants.Default_100}
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </Flex>
        {renderLineChart(false)}
      </ChartWrapper>
      <ReportingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Chart Information"
        content={renderLineChart(true)}
      />
    </>
  )
}

export default RechartsLineChart
